import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import UserInactivityModal from '@src/components/modal/user-inactivity-modal/UserInactivityModal';
import { useAuthToken, SESSION_TIMED_OUT } from '@src/hooks/useAuthToken';
import { UserActiveEvents } from '@src/constants';
import { IDLE_TIME_THROTTLE, SA_IDLE_TIME_THROTTLE, MODAL_GRACE_PERIOD } from '@src/constants/activity';
import { useRolesPermissions } from './useRolesPermission';
import { UserRoleEnum } from '@src/types';
import { useSaveTimeSpentMutation } from '@src/api/graphql-codegen';
import { usePresentationContext } from './usePresentationContext';
import { shouldSkipActivityTracking } from '@src/utils/activityUtils';
import { useUserSettings } from './useUserSettings';
import { logger } from '../utils/logger';

// Debug counter to track ActivityProvider renders
let activityProviderRenderCount = 0;

// External state storage to prevent re-renders
// This object lives outside React's render cycle and is shared across all instances
const activityState = {
  idleTimeSeconds: 0,
  activeTimeSeconds: 0,
  needsStateSync: false,
  lastStateUpdate: 0,
  state: 'Active' as 'Active' | 'Idle',
  inactivityModalOpen: false,
  isSuperAdmin: false,
  lastLogTime: 0, // Track when we last logged to reduce console spam
  pendingStateUpdate: false // Flag to prevent multiple state updates in quick succession
};

// Constants for storage keys
const ACTIVITY_STATE_UPDATE_INTERVAL = 10; // Only update React state every 10 seconds
const ALL_TABS_ACTIVITY_STATE = 'op-all-tabs-activity-state'; // Key for storing all tabs' activity states - use op- prefix to avoid conflicts
const ACTIVITY_STATE_CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes - time after which to remove inactive tabs

type ActivityContextType = {
  pauseActivityTracker: () => void
  resumeActivityTracker: () => void
};

const initialType: ActivityContextType = {
  pauseActivityTracker: () => false,
  resumeActivityTracker: () => false
};

const ActivityContext = createContext(initialType);

// Create a stable version of the ActivityProvider that doesn't re-render
export const ActivityProvider = React.memo(({children}: { children: React.ReactNode }) => {
  // Only log in development and limit frequency to reduce console spam
  if (import.meta.env.DEV) {
    const now = Date.now();
    if (now - activityState.lastLogTime > 5000) { // Only log every 5 seconds
      logger.info(`[DEBUG] ActivityProvider rendering #${++activityProviderRenderCount}`);
      activityState.lastLogTime = now;
    } else {
      // Still increment counter but don't log
      ++activityProviderRenderCount;
    }
  }
  
  // Initialize all hooks at the top level to follow React rules
  // Use the external state object for initial values to ensure consistency
  const [state, setState] = useState<'Active' | 'Idle'>(activityState.state);
  const [idleTimeSeconds, setIdleTimeSeconds] = useState<number>(activityState.idleTimeSeconds);
  const [activeTimeSeconds, setActiveTimeSeconds] = useState<number>(activityState.activeTimeSeconds);
  const [inactivityModalOpen, setInactivityModalOpen] = useState(activityState.inactivityModalOpen);
  const [, setCountryCode] = useState<string | null>(null);
  
  // These values don't change often, so we can use them directly
  const isSuperAdmin = useRolesPermissions([UserRoleEnum.SuperAdmin]);
  const { token, revokeUserSession } = useAuthToken();
  const [saveTimeSpent] = useSaveTimeSpentMutation();
  const { autoLogoutEnabled } = useUserSettings();
  
  // Update external state when isSuperAdmin changes
  useEffect(() => {
    activityState.isSuperAdmin = isSuperAdmin;
  }, [isSuperAdmin]);
  
  // Store token in a ref to prevent re-renders when token changes
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  
  // Track component mounts/unmounts for debugging
  useEffect(() => {
    logger.info('[DEBUG] ActivityProvider mounted');
    return () => {
      logger.info('[DEBUG] ActivityProvider unmounted');
    };
  }, []);
  
  // Create refs to store the latest state values
  const stateRef = useRef<'Active' | 'Idle'>(state);
  const idleTimeSecondsRef = useRef<number>(idleTimeSeconds);
  const activeTimeSecondsRef = useRef<number>(activeTimeSeconds);
  const inactivityModalOpenRef = useRef<boolean>(inactivityModalOpen);
  
  // Safely access the presentation context
  let isPresenting = false;
  try {
    const presentationContext = usePresentationContext();
    isPresenting = presentationContext?.isPresenting || false;
  } catch (error) {
    logger.info('[Activity] Error accessing presentation context:', error);
    // Default to false if context is not available
    isPresenting = false;
  }
  
  const isPresentingRef = useRef(isPresenting);
  const idleTimeThrottle = useMemo(() => (isSuperAdmin ? SA_IDLE_TIME_THROTTLE : IDLE_TIME_THROTTLE), [isSuperAdmin]);
  
  // For URL shortening app (which includes student mode), provide a minimal implementation
  // This prevents "Not Authorised" errors when students complete surveys
  const isUrlShortenApp = import.meta.env.VITE_URL_SHORTEN_APP === "true";
  
  // Check if the current URL should skip activity tracking or auto logout is disabled
  const skipActivityTracking = useMemo(() => 
    shouldSkipActivityTracking() || !autoLogoutEnabled || window.location.href === '/login', 
    [autoLogoutEnabled]
  );
  
  // Keep ref updated with latest value
  useEffect(() => {
    isPresentingRef.current = isPresenting;
  }, [isPresenting]);
  
  // Update refs and external state when state changes
  useEffect(() => {
    stateRef.current = state;
    activityState.state = state;
  }, [state]);
  
  useEffect(() => {
    idleTimeSecondsRef.current = idleTimeSeconds;
    activityState.idleTimeSeconds = idleTimeSeconds;
  }, [idleTimeSeconds]);
  
  useEffect(() => {
    activeTimeSecondsRef.current = activeTimeSeconds;
    activityState.activeTimeSeconds = activeTimeSeconds;
  }, [activeTimeSeconds]);
  
  useEffect(() => {
    inactivityModalOpenRef.current = inactivityModalOpen;
    activityState.inactivityModalOpen = inactivityModalOpen;
  }, [inactivityModalOpen]);
  
  // Extract country code from token - use tokenRef instead of token to prevent re-renders
  // This effect only needs to run once when the component mounts or when token changes
  useEffect(() => {
    if (!isUrlShortenApp && tokenRef.current) {
      try {
        // Extract country code from token (first 2 characters of user ID)
        const tokenData = JSON.parse(atob(tokenRef.current.split('.')[1]));
        const userId = tokenData?.userId || '';
        const country = userId.substring(0, 2);
        setCountryCode(country);
        
      } catch (error) {
        logger.info(error);
      }
    }
  }, [isUrlShortenApp]); // Only depend on isUrlShortenApp to prevent unnecessary re-renders

  // Handle visibility changes for all users, not just in presentation mode
  useEffect(() => {
    if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
    
    try {
      const visibility = document.visibilityState;
      
      
      if (visibility !== 'visible') {
        // If page is initially hidden, set to Idle
        setState('Idle');
      } else if (isPresenting) {
        // Special handling for presentation mode when visible
        setState('Active');
        setIdleTimeSeconds(0);
        activityState.idleTimeSeconds = 0; // Reset external state too
        setInactivityModalOpen(false);
      }
    } catch (error) {
      logger.error('[Activity] Error in visibility effect:', error);
    }
  }, [isPresenting, skipActivityTracking]);

  // Define visibility change handler outside useEffect using useCallback
  const handleVisibilityChange = useCallback(() => {
    if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
    
    const visibility = document.visibilityState;
    
    if (import.meta.env.DEV) {
      logger.debug('[Activity] Visibility changed:', visibility);
    }
    
    if (visibility !== 'visible') {
      // When tab becomes hidden, always set to Idle regardless of presentation mode
      setState('Idle');
      if (import.meta.env.DEV) {
        logger.debug('[Activity] Tab hidden, setting to Idle state');
      }
    } else if (isPresentingRef.current) {
      // Special handling for presentation mode when becoming visible
      setState('Active');
      setIdleTimeSeconds(0);
      activityState.idleTimeSeconds = 0; // Reset external state too
      setInactivityModalOpen(false);
    } else {
      // For non-presentation mode when becoming visible, reset if not already in modal
      if (!inactivityModalOpenRef.current) {
        setState('Active');
        setIdleTimeSeconds(0);
        activityState.idleTimeSeconds = 0; // Reset external state too
        if (import.meta.env.DEV) {
          logger.debug('[Activity] Tab visible again, resetting activity state');
        }
      }
    }
  }, [skipActivityTracking, setState, setIdleTimeSeconds, setInactivityModalOpen]);

  // Fix: Inactivate State issue
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        localStorage.setItem('lastHiddenTime', String(Date.now()));
      } else if (document.visibilityState === 'visible') {
        const lastHiddenTime = Number(localStorage.getItem('lastHiddenTime') || 0);
        const now = Date.now();
        const diff = (now - lastHiddenTime) / 1000; // seconds
        
        if (diff > idleTimeThrottle + MODAL_GRACE_PERIOD) {
          // Consider user idle too long → logout
          revokeUserSession();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [idleTimeThrottle, revokeUserSession]);

  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem('lastActivityTime', String(Date.now()));
    }, 10000); // every 10s when active

    return () => clearInterval(interval);
  }, []);

  // Listen for visibility changes
  useEffect(() => {
    if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
    
    const handleVisibilityChange = () => {
      const visibility = document.visibilityState;
      
      if (visibility !== 'visible') {
        // When tab becomes hidden, always set to Idle regardless of presentation mode
        setState('Idle');
        // Tab hidden - logging removed to prevent re-renders
      } else if (isPresenting) {
        // Special handling for presentation mode when becoming visible
        setState('Active');
        setIdleTimeSeconds(0);
        setInactivityModalOpen(false);
      } else {
        // For non-presentation mode when becoming visible, reset if not already in modal
        if (!inactivityModalOpen) {
          setState('Active');
          setIdleTimeSeconds(0);
          // Tab visible again - logging removed to prevent re-renders
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [skipActivityTracking, handleVisibilityChange, isPresenting, inactivityModalOpen]);

  // Function to reset activity state - used in multiple places
  const resetActivityState = useCallback(() => {
    setState('Active');
    setIdleTimeSeconds(0);
    activityState.idleTimeSeconds = 0; // Reset external state too
    setInactivityModalOpen(false);
  }, []);

  // Set up idle timer
  // We use the built-in crossTab feature but configure it to use our own storage keys
  const { pause, resume, reset, isLeader, getTabId } = useIdleTimer({
    events: UserActiveEvents,
    onAction: (event?: Event) => {
      if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
      
      const visibility = document.visibilityState;
      const tabId = getTabId();
      const isLeaderTab = isLeader();
      
      // Skip activity tracking if we're in the process of logging out
      if (inactivityModalOpen) {
        logger.debug('[Activity] Ignoring activity while modal open', {
          eventType: event?.type,
          visibility,
          tabId,
          isLeaderTab: isLeaderTab,
          timestamp: new Date().toISOString()
        });
        return;
      }
      
      logger.debug('[Activity] User activity detected', {
        eventType: event?.type,
        visibility,
        tabId,
        isLeaderTab: isLeaderTab,
        timestamp: new Date().toISOString()
      });
      
      if (event?.type === 'visibilitychange') {
        // Check visibility state
        if (visibility !== 'visible') {
          // Page is hidden or prerendering
          setState('Idle');
          // Don't reset idle time when tab becomes hidden
        } else if (isPresenting) {
          // If returning to visible while presenting, stay in current state
          // Don't reset idle time or state when in presentation mode
        } else {
          // Normal visibility change when not presenting
          resetActivityState();
        }
      } else if (visibility === 'visible') {
        // For other events when page is visible
        resetActivityState();
      }
    },
    onIdle: () => {
      if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
      
      const visibility = document.visibilityState;
      // Skip idle detection if actively presenting and visible
      if (isPresenting && visibility === 'visible') {
        logger.debug('[Activity] Ignoring idle in presentation mode', {
          visibility,
          timestamp: new Date().toISOString()
        });
        return;
      }

      logger.debug('[Activity] User went idle', {
        visibility,
        tabId: getTabId(),
        isLeaderTab: isLeader(),
        timestamp: new Date().toISOString()
      });
      // Just set state to Idle, let the time tracking effect handle the counters
      setState('Idle');
    },
    timeout: isPresenting ? 60_000 : 30_000, // 60 seconds for presentation mode, 30 seconds otherwise
    throttle: 500,
    crossTab: true, // Enable cross-tab communication for proper tab ID generation
    leaderElection: true, // Keep leader election to designate one tab as the master
    syncTimers: 1000, // Sync timeout durations between tabs (milliseconds)
    name: 'op-activity-tracker' // Custom name for the idle timer instance to avoid conflicts
  });

  // Define the beforeunload handler outside useEffect using useCallback
  const handleBeforeUnload = useCallback(() => {
    try {
      // Get the current tab's ID
      const fullTabId = getTabId();
      if (!fullTabId) return;
      
      // Extract the unique part of the tab ID
      const tabIdMatch = fullTabId.match(/^tab_(.+)$/);
      const tabId = tabIdMatch ? tabIdMatch[1] : fullTabId;
      
      // Get the current all tabs activity state
      const storedState = localStorage.getItem(ALL_TABS_ACTIVITY_STATE);
      if (!storedState) return;
      
      // Parse the stored state
      const allTabsState = JSON.parse(storedState);
      
      // Remove this tab from the all tabs state
      if (allTabsState[tabId]) {
        delete allTabsState[tabId];
        
        // Store the updated all tabs state
        localStorage.setItem(ALL_TABS_ACTIVITY_STATE, JSON.stringify(allTabsState));
        
        logger.info('[DEBUG] Removed tab from all tabs state on unload', {
          tabId,
          remainingTabs: Object.keys(allTabsState).length,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      logger.error('[Activity] Error cleaning up tab state on unload:', error);
    }
  }, [getTabId]);
  
  // Handle tab cleanup when the tab is closed
  useEffect(() => {
    // Add the event listener
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      // Remove the event listener and clean up
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Also try to clean up when the component unmounts (though this might not always run)
      handleBeforeUnload();
    };
  }, [handleBeforeUnload]);
  
  // Define the tab state update function outside useEffect using useCallback
  const updateTabState = useCallback(() => {
    if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
    
    try {
      // Get the current tab's ID from react-idle-timer
      // The tab ID from getTabId() looks like "tab_7d868ua_1751376285544"
      // We need to extract just the unique part to match what's in localStorage
      const fullTabId = getTabId();
      if (!fullTabId) {
        logger.warn('[Activity] Tab ID is null or undefined, skipping update');
        return;
      }
      
      // Extract the unique part of the tab ID (e.g., "7d868ua_1751376285544")
      // This matches the format used in tab_heartbeat and tab_leader
      const tabIdMatch = fullTabId.match(/^tab_(.+)$/);
      const tabId = tabIdMatch ? tabIdMatch[1] : fullTabId;
      
      // Log the tab ID for debugging
      if (import.meta.env.DEV) {
        logger.debug('[Activity] Using tab ID from react-idle-timer:', {
          fullTabId,
          extractedTabId: tabId
        });
      }
      
      // Get the current all tabs activity state from localStorage
      let allTabsState: Record<string, any> = {};
      try {
        const storedState = localStorage.getItem(ALL_TABS_ACTIVITY_STATE);
        if (storedState) {
          allTabsState = JSON.parse(storedState);
        }
      } catch (parseError) {
        logger.error('[Activity] Error parsing all tabs state:', parseError);
        allTabsState = {};
      }
      
      // Update this tab's state - use the full tab ID from react-idle-timer
      allTabsState[tabId] = {
        state: stateRef.current,
        idleTimeSeconds: idleTimeSecondsRef.current,
        isSuperAdmin,
        lastUpdated: Date.now(),
        isVisible: document.visibilityState === 'visible'
      };
      
      // Clean up any tabs that haven't updated in a while
      const now = Date.now();
      let cleanedUpTabs = 0;
      Object.keys(allTabsState).forEach(id => {
        if (now - allTabsState[id].lastUpdated > ACTIVITY_STATE_CLEANUP_INTERVAL) {
          delete allTabsState[id];
          cleanedUpTabs++;
        }
      });
      
      // Log if we cleaned up any tabs
      if (cleanedUpTabs > 0 && import.meta.env.DEV) {
        logger.info(`[DEBUG] Cleaned up ${cleanedUpTabs} stale tabs from all tabs state`);
      }
      
      // Store the updated all tabs state
      localStorage.setItem(ALL_TABS_ACTIVITY_STATE, JSON.stringify(allTabsState));
      
      // Only log in development and limit frequency
      if (import.meta.env.DEV && now - activityState.lastLogTime > 10000) {
        logger.info('[DEBUG] Tab activity state updated', {
          tabId,
          state: stateRef.current,
          idleTimeSeconds: idleTimeSecondsRef.current,
          isVisible: document.visibilityState === 'visible',
          allTabsCount: Object.keys(allTabsState).length,
          timestamp: new Date().toISOString()
        });
        
        // Find the most active tab (lowest idle time)
        let lowestIdleTime = idleTimeSecondsRef.current;
        let mostActiveTabId = tabId;
        
        Object.entries(allTabsState).forEach(([id, tabState]) => {
          if (tabState.idleTimeSeconds < lowestIdleTime) {
            lowestIdleTime = tabState.idleTimeSeconds;
            mostActiveTabId = id;
          }
        });
        
        logger.info('[DEBUG] Most active tab', {
          mostActiveTabId,
          lowestIdleTime,
          isThisTab: mostActiveTabId === tabId,
          timestamp: new Date().toISOString()
        });
        
        activityState.lastLogTime = now;
      }
      
      // If any tab is active (idle time below threshold), don't show modal or logout
      const anyTabActive = Object.values(allTabsState).some(
        (tabState: any) => tabState.idleTimeSeconds < idleTimeThrottle - 10
      );
      
      if (anyTabActive && inactivityModalOpenRef.current) {
        // If any tab is active and modal is open, close it
        setInactivityModalOpen(false);
        if (import.meta.env.DEV) {
          logger.info('[DEBUG] Closing inactivity modal because another tab is active');
        }
      }
      
    } catch (error) {
      logger.error('[Activity] Error updating all tabs state:', error);
    }
  }, [skipActivityTracking, getTabId, isSuperAdmin, idleTimeThrottle, setInactivityModalOpen]);
  
  // Store this tab's activity state and read all tabs' states
  // Use a debounced approach to reduce frequency of localStorage updates
  useEffect(() => {
    if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
    
    // Use a timeout to debounce updates
    const debounceTimeout = setTimeout(updateTabState, 1000);
    
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [skipActivityTracking, updateTabState, state, idleTimeSeconds]);
  
  // Define the storage change handler outside useEffect using useCallback
  const handleStorageChange = useCallback((e: StorageEvent) => {
    if (skipActivityTracking) return;
    if (e.key !== ALL_TABS_ACTIVITY_STATE || !e.newValue) return;
    
    // Process the storage change after a short delay to avoid multiple rapid updates
    try {
      const allTabsState = JSON.parse(e.newValue!);
      // Extract the unique part of the tab ID
      const fullTabId = getTabId();
      const tabIdMatch = fullTabId.match(/^tab_(.+)$/);
      const tabId = tabIdMatch ? tabIdMatch[1] : fullTabId;
      const now = Date.now();
      
      // Only log in development and limit frequency
      if (import.meta.env.DEV && now - activityState.lastLogTime > 10000) {
        logger.debug('[Activity] Detected change in all tabs state', {
          currentTabId: tabId,
          allTabIds: Object.keys(allTabsState),
          tabsCount: Object.keys(allTabsState).length,
          timestamp: new Date().toISOString()
        });
        activityState.lastLogTime = now;
      }
      
      // Find the most active tab (lowest idle time)
      let lowestIdleTime = Number.MAX_SAFE_INTEGER;
      let mostActiveTabId = '';
      
      Object.entries(allTabsState).forEach(([id, tabState]: [string, any]) => {
        if (tabState.idleTimeSeconds < lowestIdleTime) {
          lowestIdleTime = tabState.idleTimeSeconds;
          mostActiveTabId = id;
        }
      });
      
      // If any tab is active (idle time below threshold), don't show modal or logout
      const anyTabActive = Object.values(allTabsState).some(
        (tabState: any) => tabState.idleTimeSeconds < idleTimeThrottle - 10
      );
      
      if (anyTabActive && inactivityModalOpenRef.current) {
        // If any tab is active and modal is open, close it
        setInactivityModalOpen(false);
        if (import.meta.env.DEV) {
          logger.info('[DEBUG] Closing inactivity modal because another tab is active');
        }
      }
      
    } catch (error) {
      logger.error('[Activity] Error handling storage change:', error);
    }
  }, [skipActivityTracking, getTabId, idleTimeThrottle, setInactivityModalOpen]);
  
  // Listen for changes to the all tabs activity state
  useEffect(() => {
    if (skipActivityTracking) return;
    
    // Create a debounced storage event handler to reduce processing frequency
    let storageChangeTimeout: NodeJS.Timeout | null = null;
    
    const debouncedStorageHandler = (e: StorageEvent) => {
      if (e.key !== ALL_TABS_ACTIVITY_STATE || !e.newValue) return;
      
      // Clear any existing timeout to debounce multiple rapid storage events
      if (storageChangeTimeout) {
        clearTimeout(storageChangeTimeout);
      }
      
      // Process the storage change after a short delay
      storageChangeTimeout = setTimeout(() => {
        handleStorageChange(e);
      }, 500); // 500ms debounce
    };
    
    window.addEventListener('storage', debouncedStorageHandler);
    
    return () => {
      window.removeEventListener('storage', debouncedStorageHandler);
      if (storageChangeTimeout) {
        clearTimeout(storageChangeTimeout);
      }
    };
  }, [skipActivityTracking, handleStorageChange]);


  // Global interval timer that doesn't cause re-renders
  useEffect(() => {
    if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
    
    // Create a global timer outside of React's render cycle
    const globalTimerInterval = setInterval(() => {
      const currentState = stateRef.current;
      const visibility = document.visibilityState;
      const isPresentingCurrent = isPresentingRef.current;
      const now = Date.now();
      
      try {
        // Condition for idle time tracking:
        // Count idle time when either:
        // 1. The user is in 'Idle' state (but not in presentation mode with visible tab), OR
        // 2. The tab is not visible (hidden)
        if ((currentState === 'Idle' && !(isPresentingCurrent && visibility === 'visible')) || visibility !== 'visible') {
          // Skip idle time tracking in presentation mode when tab is visible
          if (isPresentingCurrent && visibility === 'visible') {
            if (import.meta.env.DEV && now - activityState.lastLogTime > 60000) {
              logger.debug('[Activity] Skipping idle time tracking in presentation mode');
            } else {
              // Normal idle time tracking - use ref to avoid re-renders
              idleTimeSecondsRef.current = idleTimeSecondsRef.current + 1;
              
              // Log idle time every 5 minutes for debugging - removed console.warn to prevent re-renders
              // Reduce logging frequency for idle time accumulation - removed console.warn to prevent re-renders
              
              // Only update state when we need to trigger UI changes (modal or logout)
              if (idleTimeSecondsRef.current >= idleTimeThrottle - 1 && !inactivityModalOpenRef.current) {
                setIdleTimeSeconds(idleTimeSecondsRef.current);
              } else if (idleTimeSecondsRef.current >= idleTimeThrottle + MODAL_GRACE_PERIOD) {
                setIdleTimeSeconds(idleTimeSecondsRef.current);
              }
            }
          } else {
            // Normal idle time tracking - update external state only
            activityState.idleTimeSeconds += 1;
            
            // Reduce logging frequency to improve performance
            // Log only in development or at significant intervals
            if (import.meta.env.DEV && 
                activityState.idleTimeSeconds % 300 === 0 && 
                now - activityState.lastLogTime > 60000) {
              logger.debug(`[Activity] Idle time: ${activityState.idleTimeSeconds} seconds, threshold: ${idleTimeThrottle} seconds`);
              activityState.lastLogTime = now;
            }
            
            // Only trigger React state updates when needed and not already pending
            if (!activityState.pendingStateUpdate) {
              const needsStateUpdate = 
                // Update state when approaching timeout to show modal
                (activityState.idleTimeSeconds >= idleTimeThrottle - 2 && activityState.idleTimeSeconds <= idleTimeThrottle) || 
                // Update state for logout
                (activityState.idleTimeSeconds >= idleTimeThrottle + MODAL_GRACE_PERIOD - 1 && activityState.idleTimeSeconds <= idleTimeThrottle + MODAL_GRACE_PERIOD + 1) ||
                // Update state occasionally for localStorage persistence
                (activityState.idleTimeSeconds % ACTIVITY_STATE_UPDATE_INTERVAL === 0) ||
                // Ensure we update at least every 30 seconds
                (now - activityState.lastStateUpdate >= 30000);
                
              if (needsStateUpdate) {
                // Set pending flag to prevent multiple updates
                activityState.pendingStateUpdate = true;
                
                // Schedule state update outside of this interval
                setTimeout(() => {
                  setIdleTimeSeconds(activityState.idleTimeSeconds);
                  activityState.lastStateUpdate = now;
                  activityState.pendingStateUpdate = false;
                }, 0);
              }
            }
          }
        } else {
          // Active time tracking - update external state only
          if (activeTimeSecondsRef.current > 0 && currentState === 'Idle') {
            // If we're transitioning to idle and have active time, save it
            if (!activityState.pendingStateUpdate) {
              activityState.pendingStateUpdate = true;
              setTimeout(async () => {
                try {
                  await saveTimeSpent({variables: {input: {timeSpent: activeTimeSecondsRef.current}}});
                } catch (error) {
                  logger.error('Failed to save time spent:', error);
                }
                activityState.activeTimeSeconds = 0;
                setActiveTimeSeconds(0);
                activityState.pendingStateUpdate = false;
              }, 0);
            }
          } else {
            activityState.activeTimeSeconds += 1;
            
            // Only update React state occasionally and not already pending
            if (!activityState.pendingStateUpdate && 
                (activityState.activeTimeSeconds % ACTIVITY_STATE_UPDATE_INTERVAL === 0 || 
                (now - activityState.lastStateUpdate >= 30000))) {
              activityState.pendingStateUpdate = true;
              setTimeout(() => {
                setActiveTimeSeconds(activityState.activeTimeSeconds);
                activityState.lastStateUpdate = now;
                activityState.pendingStateUpdate = false;
              }, 0);
            }
          }
        }
      } catch (error) {
        logger.error('[Activity] Error in time tracking interval:', error);
      }
    }, 1000);

    return () => {
      clearInterval(globalTimerInterval);
    };
  }, [idleTimeThrottle, saveTimeSpent, skipActivityTracking]);

  // Handle idle time and modal visibility - based on this tab's state and all tabs' states
  useEffect(() => {
    if (skipActivityTracking) return; // Skip for pages that should skip activity tracking
    
    if (state === 'Idle' && idleTimeSeconds > 0) {
      const visibility = document.visibilityState;
      // Extract the unique part of the tab ID
      const fullTabId = getTabId();
      const tabIdMatch = fullTabId.match(/^tab_(.+)$/);
      const tabId = tabIdMatch ? tabIdMatch[1] : fullTabId;
      
      // Force warn logging to help diagnose the issue - removed console.warn to prevent re-renders
      
      if (isPresenting) {
        // In presentation mode, never log out regardless of tab visibility
        // This ensures teachers can present content without interruption
        logger.debug('[Activity] Skipping logout in presentation mode', {
          idleTimeSeconds,
          idleTimeThrottle,
          visibility,
          timestamp: new Date().toISOString()
        });
        // No logout action in presentation mode
        return;
      }
      
      // Check if any tab is active (has low idle time)
      let allTabsState: Record<string, any> = {};
      let anyTabActive = false;
      let mostActiveTabId = '';
      let lowestIdleTime = Number.MAX_SAFE_INTEGER;
      
      try {
        const storedState = localStorage.getItem(ALL_TABS_ACTIVITY_STATE);
        if (storedState) {
          allTabsState = JSON.parse(storedState);
          
          // Find the most active tab (lowest idle time)
          Object.entries(allTabsState).forEach(([id, tabState]: [string, any]) => {
            if (tabState.idleTimeSeconds < lowestIdleTime) {
              lowestIdleTime = tabState.idleTimeSeconds;
              mostActiveTabId = id;
            }
          });
          
          // Check if any tab is active (idle time below threshold)
          anyTabActive = Object.values(allTabsState).some(
            (tabState: any) => tabState.idleTimeSeconds < idleTimeThrottle - 10
          );
          
          // Log the most active tab for debugging
          if (idleTimeSeconds % 60 === 0) { // Log every minute
            logger.warn('[Activity] Most active tab check', {
              environmentEnv: import.meta.env.MODE,
              mostActiveTabId,
              lowestIdleTime,
              isThisTab: mostActiveTabId === tabId,
              anyTabActive,
              timestamp: new Date().toISOString()
            });
          }
        }
      } catch (error) {
        logger.error('[Activity] Error checking all tabs state:', error);
      }
      
      // If any tab is active, don't show modal or logout
      if (anyTabActive) {
        if (inactivityModalOpen) {
          setInactivityModalOpen(false);
          logger.info('[DEBUG] Closing inactivity modal because another tab is active');
        }
        return;
      }
      
      // Only proceed with logout if this is the most active tab
      // This prevents multiple tabs from trying to log out at the same time
      if (mostActiveTabId !== '' && mostActiveTabId !== tabId) {
        if (import.meta.env.DEV) {
          logger.debug('[Activity] Skipping logout in non-most-active tab', {
            thisTabId: tabId,
            mostActiveTabId,
            timestamp: new Date().toISOString()
          });
        }
        return;
      }
      
      // If this is the visible tab, handle showing the modal
      if (visibility === 'visible') {
        // Show modal 1 second before timeout to avoid race conditions
        if (idleTimeSeconds >= idleTimeThrottle - 1 && !inactivityModalOpen) {
          // Showing inactivity modal - removed console.warn to prevent re-renders
          setInactivityModalOpen(true);
        }
        
        // Handle logout if grace period has elapsed
        if (idleTimeSeconds >= idleTimeThrottle + MODAL_GRACE_PERIOD) {
          // Session timeout - removed console.warn to prevent re-renders
          localStorage.setItem(SESSION_TIMED_OUT, `${Date.now()}`);
          revokeUserSession();
        }
      } else {
        // For background tabs, log idle time less frequently
        if (idleTimeSeconds % 30 === 0) {
          logger.debug('[Activity] Background tab tracking idle time', {
            idleTimeSeconds,
            idleTimeThrottle,
            tabId,
            timestamp: new Date().toISOString()
          });
        }
      }
    }
  }, [idleTimeSeconds, isPresenting, revokeUserSession, state, inactivityModalOpen, idleTimeThrottle, skipActivityTracking, getTabId]);

  // Create a ref to track if we're already processing a force check
  const isProcessingForceCheck = useRef(false);
  
  // Consolidated force check for idle timeout (runs every 10 seconds)
  useEffect(() => {
    // Skip for pages that should skip activity tracking
    if (skipActivityTracking) return;
    
    // Only the leader tab should run the force check
    if (!isLeader()) {
      if (import.meta.env.DEV) {
        logger.debug('[Activity] Skipping force check in non-leader tab', {
          tabId: getTabId(),
          timestamp: new Date().toISOString()
        });
      }
      return;
    }
    
    const forceCheckInterval = setInterval(() => {
      // Skip if we're already processing a force check
      if (isProcessingForceCheck.current) {
        return;
      }
      
      // Check again if we should skip activity tracking (URL might have changed)
      if (skipActivityTracking) {
        if (import.meta.env.DEV) {
          logger.debug('[Activity] Skipping force check for non-tracked URL');
        }
        return;
      }

      // Force logout after check lastActivity
      const lastActivity = Number(localStorage.getItem('lastActivityTime') || 0);
      if (Date.now() - lastActivity > (idleTimeThrottle + MODAL_GRACE_PERIOD) * 1000) {
        revokeUserSession();
      }
      
      // Get the current state values from refs
      const currentState = stateRef.current;
      const currentIdleTimeSeconds = idleTimeSecondsRef.current;
      const currentInactivityModalOpen = inactivityModalOpenRef.current;
      const visibility = document.visibilityState;
      const isPresentingCurrent = isPresentingRef.current;
      
      // Log the force check for debugging (using console.debug to prevent re-renders)
      logger.info('[Activity] Force check running', {
        state: currentState,
        idleTimeSeconds: currentIdleTimeSeconds,
        idleTimeThrottle,
        inactivityModalOpen: currentInactivityModalOpen,
        isPresenting: isPresentingCurrent,
        visibility,
        timestamp: new Date().toISOString()
      });
      
      // Skip all checks if in presentation mode - never log out in presentation mode
      if (isPresentingCurrent) {
        logger.debug('[Activity] Skipping force check in presentation mode');
        return;
      }
      
      // Set processing flag to prevent concurrent force checks
      isProcessingForceCheck.current = true;
      
      // Use setTimeout to process the force check outside the interval callback
      setTimeout(() => {
        try {
          // Get the current state values from refs
          const currentState = stateRef.current;
          const currentIdleTimeSeconds = activityState.idleTimeSeconds; // Use external state
          const currentInactivityModalOpen = inactivityModalOpenRef.current;
          const visibility = document.visibilityState;
          const isPresentingCurrent = isPresentingRef.current;
          // Extract the unique part of the tab ID
          const fullTabId = getTabId();
          const tabIdMatch = fullTabId.match(/^tab_(.+)$/);
          const tabId = tabIdMatch ? tabIdMatch[1] : fullTabId;
          const now = Date.now();
          
          // Only log periodically to reduce console spam
          if (now - activityState.lastLogTime > 60000) { // Log at most once per minute
            logger.warn('[Activity] Force check running (leader tab)', {
              tabId,
              isLeaderTab: isLeader(),
              state: currentState,
              idleTimeSeconds: currentIdleTimeSeconds,
              idleTimeThrottle,
              inactivityModalOpen: currentInactivityModalOpen,
              isPresenting: isPresentingCurrent,
              visibility,
              timestamp: new Date().toISOString(),
              activityProviderRender: activityProviderRenderCount,
              tokenAvailable: !!token,
              tokenFirstChars: token ? token.substring(0, 10) + '...' : 'none'
            });
            activityState.lastLogTime = now;
          }
          
          // Skip all checks if in presentation mode - never log out in presentation mode
          if (isPresentingCurrent) {
            if (import.meta.env.DEV) {
              logger.debug('[Activity] Skipping force check in presentation mode');
            }
            isProcessingForceCheck.current = false;
            return;
          }
          
          // Only proceed with checks for non-presentation mode
          
          // Check if we should show the inactivity modal
          // Show modal 1 second before timeout to avoid race conditions
          if (currentState === 'Idle' && 
              currentIdleTimeSeconds >= idleTimeThrottle - 1 && 
              !currentInactivityModalOpen) {
            
            logger.warn('[Activity] Force check - showing inactivity modal (leader tab)', {
              idleTimeSeconds: currentIdleTimeSeconds,
              idleTimeThrottle,
              visibility,
              tabId,
              timestamp: new Date().toISOString()
            });
            
            // Force update the inactivity modal state
            setInactivityModalOpen(true);
            
            // Verify the state was updated by using a timeout
            setTimeout(() => {
              if (!inactivityModalOpenRef.current) {
                logger.error('[Activity] Modal state failed to update - retrying', {
                  currentModalState: inactivityModalOpenRef.current,
                  timestamp: new Date().toISOString()
                });
                // Force another attempt
                setInactivityModalOpen(true);
              }
              isProcessingForceCheck.current = false;
            }, 500);
            return; // Exit early to let the nested timeout handle the flag
          }
          
          // Check if we need to force logout due to extended inactivity
          if (currentState === 'Idle' && 
              currentIdleTimeSeconds >= idleTimeThrottle + MODAL_GRACE_PERIOD) {
            
            // Check if any tab is active before logging out
            let allTabsState: Record<string, any> = {};
            let anyTabActive = false;
            let mostActiveTabId = '';
            let lowestIdleTime = Number.MAX_SAFE_INTEGER;
            
            try {
              const storedState = localStorage.getItem(ALL_TABS_ACTIVITY_STATE);
              if (storedState) {
                allTabsState = JSON.parse(storedState);
                
                // Find the most active tab (lowest idle time)
                Object.entries(allTabsState).forEach(([id, tabState]: [string, any]) => {
                  if (tabState.idleTimeSeconds < lowestIdleTime) {
                    lowestIdleTime = tabState.idleTimeSeconds;
                    mostActiveTabId = id;
                  }
                });
                
                // Check if any tab is active (idle time below threshold)
                anyTabActive = Object.values(allTabsState).some(
                  (tabState: any) => tabState.idleTimeSeconds < idleTimeThrottle - 10
                );
              }
            } catch (error) {
              logger.error('[Activity] Error checking all tabs state in force check:', error);
            }
            
            // If any tab is active, don't log out
            if (anyTabActive) {
              logger.warn('[Activity] Skipping logout because another tab is active', {
                timestamp: new Date().toISOString()
              });
              isProcessingForceCheck.current = false;
              return;
            }
            
            // Only proceed with logout if this is the most active tab
            if (mostActiveTabId !== '' && mostActiveTabId !== tabId) {
              logger.warn('[Activity] Skipping logout in non-most-active tab', {
                thisTabId: tabId,
                mostActiveTabId,
                timestamp: new Date().toISOString()
              });
              isProcessingForceCheck.current = false;
              return;
            }
            
            logger.warn('[Activity] Force check - session timeout (leader tab)', {
              idleTimeSeconds: currentIdleTimeSeconds,
              idleTimeThrottle,
              graceTimeElapsed: currentIdleTimeSeconds - idleTimeThrottle,
              visibility,
              tabId,
              mostActiveTabId,
              timestamp: new Date().toISOString()
            });
            
            // Store the session timeout flag and revoke the session
            try {
              localStorage.setItem(SESSION_TIMED_OUT, `${Date.now()}`);
              // Force logout
              revokeUserSession();
              logger.warn('[Activity] Session revoked due to inactivity');
            } catch (error) {
              logger.error('[Activity] Failed to revoke session:', error);
            }
            isProcessingForceCheck.current = false;
            return; // Exit early after logout
          }
          
          // Reset processing flag if we didn't take any action
          isProcessingForceCheck.current = false;
        } catch (error) {
          logger.error('[Activity] Error in force check:', error);
          isProcessingForceCheck.current = false;
        }
      }, 0);
    }, 10000); // Check every 10 seconds (compromise between 5s and 60s)
    
    return () => clearInterval(forceCheckInterval);
  }, [idleTimeThrottle, revokeUserSession, skipActivityTracking, token, isLeader, getTabId]);

  const onStayLoggedInClick = useCallback(() => {
    resetActivityState();
    reset();
    logger.debug('[Activity] User clicked "Stay Logged In"');
  }, [reset, resetActivityState]);

  const onLogoutClick = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation(); // Stop event from bubbling
    logger.debug('[Activity] Manual logout from modal', {
      timestamp: new Date().toISOString()
    });
    localStorage.setItem(SESSION_TIMED_OUT, `${Date.now()}`);
    revokeUserSession();
  }, [revokeUserSession]);

  // Always define all hooks unconditionally to avoid "rendered more hooks than previous render" errors
  const contextValue = useMemo(() => ({
    pauseActivityTracker: pause,
    resumeActivityTracker: resume
  }), [pause, resume]);

  // Return the appropriate provider based on the app type
  return (
    <ActivityContext.Provider value={isUrlShortenApp ? {
      pauseActivityTracker: () => {},
      resumeActivityTracker: () => {}
    } : contextValue}>
      {children}
      {!isUrlShortenApp && !!inactivityModalOpen && (
        <UserInactivityModal
          open={inactivityModalOpen}
          onOkAction={onStayLoggedInClick}
          onCancelAction={onLogoutClick}
        />
      )}
    </ActivityContext.Provider>
  );
});

export const useActivityTracker = () => useContext(ActivityContext);
